import React, { useState, useEffect, useMemo } from 'react'
import {
    Button,
    Card,
    Col,
    Form,
    Icon,
    message,
    Row,
    Select,
    Skeleton,
    Spin,
    Switch,
    Table,
    DatePicker,
    Empty
} from 'antd'
import moment from 'moment'
import { connect } from 'react-redux'
import { saveAs } from 'file-saver'
import * as XLSX from 'xlsx'
import axios from 'util/Api'
import { Link } from 'react-router-dom'
import { fetchBowserAssets } from '../../../appRedux/actions/Asset'

import { getBowserReport, downloadBowserReport } from '../../../appRedux/actions/Fuel'
import { searchGlobalAssetData } from '../../../appRedux/actions'
import AssetSearch from '../../../components/AssetSearch'
import './style.css'

const { RangePicker } = DatePicker

function BowserReport({ form, loading, bowserAssets, bowserReportData, assetData, assetSearchLoading, authUser, fetchBowserAssets, getBowserReport, downloadBowserReport, searchGlobalAssetData }) {
    const [downloadLoading, setDownloadLoading] = useState(false)
    const [selectedAssets, setSelectedAssets] = useState([])
    const [period, setPeriod] = useState(null)
    const [dayWiseSummary, setDayWiseSummary] = useState(false)

    useEffect(() => {
        // Load initial data — bowserAssets is already restricted server-side to
        // assets whose equipmentSubType.assetType.isBowser === true.
        fetchBowserAssets()
    }, [])

    // Derive bowser-only asset type / sub type / model option lists directly from
    // the already-bowser-filtered bowserAssets list, instead of the generic
    // (shared) asset-filters list which includes every asset type in the org.
    const bowserAssetTypeOptions = useMemo(() => {
        const map = {}
        ;(bowserAssets || []).forEach(item => {
            if (item && item.assetType && item.assetType._id) {
                map[item.assetType._id] = item.assetType
            }
        })
        return Object.values(map)
    }, [bowserAssets])

    const bowserAssetSubTypeOptions = useMemo(() => {
        const map = {}
        ;(bowserAssets || []).forEach(item => {
            if (item && item.subType && item.subType._id) {
                map[item.subType._id] = item.subType
            }
        })
        return Object.values(map)
    }, [bowserAssets])

    const bowserAssetModelOptions = useMemo(() => {
        const map = {}
        ;(bowserAssets || []).forEach(item => {
            if (item && item.model && item.model._id) {
                map[item.model._id] = item.model
            }
        })
        return Object.values(map)
    }, [bowserAssets])

    const bowserAssetIdSet = useMemo(() => {
        return new Set((bowserAssets || []).map(item => item && item._id && item._id.toString()).filter(Boolean))
    }, [bowserAssets])

    // AssetModel doesn't carry its own assetType reference, only assetSubType — build a
    // lookup so the model dropdown can still be narrowed when only Asset Type is selected.
    const subTypeIdToAssetTypeId = useMemo(() => {
        const map = {}
        bowserAssetSubTypeOptions.forEach(subType => {
            if (subType && subType._id) {
                map[subType._id.toString()] = subType.assetType && subType.assetType.toString ? subType.assetType.toString() : subType.assetType
            }
        })
        return map
    }, [bowserAssetSubTypeOptions])

    // Break the already-fetched bowser report data down into per-day, per-bowser
    // summary rows — computed entirely client-side from the detailed transaction
    // records already present in bowserReportData, no extra API calls needed.
    const dayWiseGroups = useMemo(() => {
        if (!bowserReportData || Object.keys(bowserReportData).length === 0) {
            return []
        }
        const dayMap = {}
        Object.keys(bowserReportData).forEach(bowserId => {
            const bowserEntry = bowserReportData[bowserId]
            const bowser = bowserEntry && bowserEntry.data && bowserEntry.data.bowser
            // Order records the same way the backend calculates balances: by
            // (datetime, _id). datetime is minute-truncated so ties are common; _id
            // (monotonic with insertion order) breaks them deterministically, keeping
            // the displayed running balance in sync with the stored ledger.
            const records = [...((bowserEntry && bowserEntry.records) || [])].sort((a, b) => {
                const dt = (a.datetime || 0) - (b.datetime || 0)
                if (dt !== 0) return dt
                const aId = (a._id || '') + ''
                const bId = (b._id || '') + ''
                return aId < bId ? -1 : aId > bId ? 1 : 0
            })

            records.forEach(record => {
                const dateKey = moment(record.date).format('YYYY-MM-DD')
                if (!dayMap[dateKey]) dayMap[dateKey] = {}
                if (!dayMap[dateKey][bowserId]) {
                    dayMap[dateKey][bowserId] = {
                        bowserAsset: bowser,
                        records: [],
                        openingBalance: null,
                        fuelAdded: 0,
                        fuelIssued: 0,
                        balance: 0,
                    }
                }
                const bucket = dayMap[dateKey][bowserId]
                if (bucket.openingBalance === null) {
                    // Reverse this record's own delta to get the balance immediately
                    // before it — i.e. the closing balance carried over from the previous day.
                    bucket.openingBalance = (record.balance || 0) - (record.quantityAdded || 0) + (record.quantityRemoved || 0)
                }
                bucket.records.push(record)
                bucket.fuelAdded += record.quantityAdded || 0
                bucket.fuelIssued += record.quantityRemoved || 0
                bucket.balance = record.balance || 0 // records are date-sorted, so the last write wins
            })
        })

        return Object.keys(dayMap).sort().map(dateKey => ({
            date: dateKey,
            rows: Object.keys(dayMap[dateKey]).map(bowserId => ({
                key: `${dateKey}-${bowserId}`,
                bowserAsset: dayMap[dateKey][bowserId].bowserAsset,
                bowserAssetCode: dayMap[dateKey][bowserId].bowserAsset && dayMap[dateKey][bowserId].bowserAsset.assetCode,
                openingBalance: dayMap[dateKey][bowserId].openingBalance || 0,
                fuelAdded: dayMap[dateKey][bowserId].fuelAdded,
                fuelIssued: dayMap[dateKey][bowserId].fuelIssued,
                balance: dayMap[dateKey][bowserId].balance,
                childrenData: dayMap[dateKey][bowserId].records,
            }))
        }))
    }, [bowserReportData])

    // Sync period state with form field when form is reset or edited
    useEffect(() => {
        const formPeriod = form.getFieldValue('periodFromTo')
        if (formPeriod && formPeriod.length === 2) {
            setPeriod(formPeriod)
        }
    }, [form])





    const handleLocationChange = (values) => {
        // If "all" is selected, clear other selections
        if (values && values.includes('all')) {
            form.setFieldsValue({ location: ['all'] })
        }
    }

    const handleSubLocationChange = (values) => {
        // If "all" is selected, clear other selections
        if (values && values.includes('all')) {
            form.setFieldsValue({ subLocation: ['all'] })
        }
    }

    const handleAssetTypeChange = (values) => {
        form.setFieldsValue({ assetSubType: undefined, assetModel: undefined, assetCode: undefined })
        // If "all" is selected, clear other selections
        if (values && values.includes('all')) {
            form.setFieldsValue({ assetType: ['all'] })
        }
    }

    const handleAssetSubTypeChange = (values) => {
        form.setFieldsValue({ assetModel: undefined, assetCode: undefined })
        // If "all" is selected, clear other selections
        if (values && values.includes('all')) {
            form.setFieldsValue({ assetSubType: ['all'] })
        }
    }

    const handleAssetModelChange = (values) => {
        form.setFieldsValue({ assetCode: undefined })
        setSelectedAssets([])
        // If "all" is selected, clear other selections
        if (values && values.includes('all')) {
            form.setFieldsValue({ assetModel: ['all'] })
        }
    }

    const handleAssetSelect = (val) => {
        if (val) {
            setSelectedAssets(val)
        }
    }

    const handlePeriodChange = (dates) => {
        setPeriod(dates)
        // Also update the form field value
        form.setFieldsValue({ periodFromTo: dates })
    }

    const handleSubmit = e => {
        e.preventDefault()
        e.stopPropagation()
        form.validateFields((err, values) => {
            if (err) {
                // Form validation failed
            } else {
                if (!values.periodFromTo || values.periodFromTo.length !== 2) {
                    message.error("Please select a valid period!")
                    return
                }

                if (moment(values.periodFromTo[0]).isSameOrBefore(values.periodFromTo[1])) {
                    const payload = {
                        locations: values.location && values.location.includes('all') ? [] : values.location,
                        subLocations: values.subLocation && values.subLocation.includes('all') ? [] : values.subLocation,
                        assetModels: values.assetModel && values.assetModel.includes('all') ? [] : values.assetModel,
                        assetTypes: values.assetType && values.assetType.includes('all') ? [] : values.assetType,
                        equipmentSubTypes: values.assetSubType && values.assetSubType.includes('all') ? [] : values.assetSubType,
                        assets: selectedAssets,
                        periodFrom: moment(values.periodFromTo[0]).startOf('day').valueOf(),
                        periodTo: moment(values.periodFromTo[1]).endOf('day').valueOf(),
                    }
                    // console.log('payload =>', payload)
                    generateBowserReport(payload)
                } else {
                    message.error("Period From couldn't come earlier than Period To!")
                }
            }
        })
    }

    const generateBowserReport = async (payload) => {
        try {
            await getBowserReport(payload)
        } catch (error) {
            message.error('Failed to generate report. Please try again.')
            console.error('Error generating bowser report:', error)
        }
    }

    const downloadDayWiseReport = () => {
        // Excel (not CSV) so that, per day per bowser, we can show the day summary row
        // followed immediately by all of that day's individual transaction records —
        // the same grouping shown on the page — in a single readable sheet.
        const rows = []
        dayWiseGroups.forEach(group => {
            group.rows.forEach(row => {
                const bowserCode = (row.bowserAsset && (row.bowserAsset.assetCode || '-') + '/' + (row.bowserAsset.ownerCode || '-') + '/' + (row.bowserAsset.altName || '-')) || '-'

                rows.push({
                    'Date': moment(group.date).format('DD-MM-YYYY'),
                    'Bowser Asset Code': bowserCode,
                    'Row Type': 'DAY SUMMARY',
                    'Opening Balance (L)': Number(row.openingBalance || 0).toFixed(2),
                    'Fuel Added (L)': Number(row.fuelAdded || 0).toFixed(2),
                    'Fuel Issued (L)': Number(row.fuelIssued || 0).toFixed(2),
                    'Balance (L)': Number(row.balance || 0).toFixed(2),
                    'Particulars': '',
                    'VCH': '',
                    'Dispensed To Asset': '',
                })

                ;(row.childrenData || []).forEach(record => {
                    const isRefill = ['REFILL', 'REFILL_ADJ'].includes(record.bowserRefuelingType)
                    rows.push({
                        'Date': moment(record.date).format('DD-MM-YYYY HH:mm A'),
                        'Bowser Asset Code': bowserCode,
                        'Row Type': 'Transaction',
                        'Opening Balance (L)': '',
                        'Fuel Added (L)': isRefill ? Number(record.quantityAdded || 0).toFixed(2) : '',
                        'Fuel Issued (L)': !isRefill ? Number(record.quantityRemoved || 0).toFixed(2) : '',
                        'Balance (L)': Number(record.balance || 0).toFixed(2),
                        'Particulars': record.bowserRefuelingType || '',
                        'VCH': (record.refFuelRecord && record.refFuelRecord.voucherNo) || (record.refFuelAdjustment && record.refFuelAdjustment.voucherNo) || '',
                        'Dispensed To Asset': (record.refAsset && (record.refAsset.assetCode || '-') + '/' + (record.refAsset.ownerCode || '-') + '/' + (record.refAsset.altName || '-')) || '',
                    })
                })
            })
        })

        if (rows.length === 0) {
            message.error('No day-wise records available to download.')
            return
        }

        const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
        const worksheet = XLSX.utils.json_to_sheet(rows)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Bowser Day-wise Report')
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([excelBuffer], { type: fileType })
        saveAs(blob, `bowser-daywise-report-${new Date().getTime()}.xlsx`)

        message.success(`Report downloaded successfully! ${rows.length} rows exported.`)
    }

    const downloadReport = async () => {
        try {
            setDownloadLoading(true)
            // console.log('bowserReportData =>', bowserReportData)

            if (dayWiseSummary) {
                downloadDayWiseReport()
                return
            }

            // Use the current bowserReportData instead of making a new API call
            if (bowserReportData && Object.keys(bowserReportData).length > 0) {
                // Extract all expanded children records from all bowser assets
                const allDetailedRecords = []

                Object.keys(bowserReportData).forEach(key => {
                    const bowserData = bowserReportData[key]
                    const bowserCode = bowserData.data.bowser.assetCode

                    // Get children data (detailed records) for this bowser
                    if (bowserData.records && bowserData.records.length > 0) {
                        bowserData.records.forEach(record => {
                            allDetailedRecords.push({
                                'Bowser Asset Code': (bowserCode || '-') + '/' + (bowserData.data.bowser.ownerCode || '-') + '/' + (bowserData.data.bowser.altName || '-'),
                                'Date': moment(record.date).format('DD-MM-YYYY HH:mm A'),
                                'Particulars': record.bowserRefuelingType || '',
                                'VCH': (record.refFuelRecord && record.refFuelRecord.voucherNo) || (record.refFuelAdjustment && record.refFuelAdjustment.voucherNo) || '',
                                'Asset Code': record.refAsset && (record.refAsset.assetCode || '-') + '/' + (record.refAsset.ownerCode || '-') + '/' + (record.refAsset.altName || '-'),
                                'Qty': ['REFILL', 'REFILL_ADJ'].includes(record.bowserRefuelingType) ? record.quantityAdded : record.quantityRemoved,
                                'Balance': record.balance
                            })
                        })
                    }
                })

                if (allDetailedRecords.length === 0) {
                    message.error('No detailed records available to download.')
                    return
                }

                const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
                const worksheet = XLSX.utils.json_to_sheet(allDetailedRecords)
                const workbook = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Bowser Report')
                const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
                const blob = new Blob([excelBuffer], { type: fileType })
                saveAs(blob, `bowser-detailed-report-${new Date().getTime()}.xlsx`)

                message.success(`Report downloaded successfully! ${allDetailedRecords.length} detailed records exported.`)
            } else {
                message.error('No data available to download. Please generate a report first.')
            }
        } catch (error) {
            message.error('Failed to download report. Please try again.')
            console.error('Error downloading bowser report:', error)
        } finally {
            setDownloadLoading(false)
        }
    }

    const getTableColumns = (dayRange) => {
        return [
            {
                title: 'Bowser Asset Code',
                dataIndex: 'bowserAssetCode',
                key: 'bowserAssetCode',
                width: 200,
                render: (_, record) => {
                    let fromTs, toTs
                    if (dayRange) {
                        [fromTs, toTs] = dayRange
                    } else {
                        const currentPeriod = form.getFieldValue('periodFromTo')
                        fromTs = currentPeriod && currentPeriod[0] && moment(currentPeriod[0]).startOf('day').valueOf()
                        toTs = currentPeriod && currentPeriod[1] && moment(currentPeriod[1]).endOf('day').valueOf()
                    }
                    return <div>
                        {fromTs && toTs ? (
                            <Link to={`/fuel/bowser-transaction-report/${record.bowserAsset._id}/${fromTs}/${toTs}`}>
                                {record.bowserAsset && (record.bowserAsset.assetCode || '-') + '/' + (record.bowserAsset.ownerCode || '-') + '/' + (record.bowserAsset.altName || '-')}
                            </Link>
                        ) : (
                            <span style={{ color: '#999', cursor: 'not-allowed' }}>
                                {record.bowserAsset && (record.bowserAsset.assetCode || '-') + '/' + (record.bowserAsset.ownerCode || '-') + '/' + (record.bowserAsset.altName || '-')}
                            </span>
                        )}
                    </div>
                }
            },
            {
                title: 'Opening Balance',
                dataIndex: 'openingBalance',
                key: 'openingBalance',
                render: (value) => <div>{Number(value).toFixed(2) || 0} L</div>
            },
            {
                title: 'Fuel Added during the Period',
                dataIndex: 'fuelAdded',
                key: 'fuelAdded',
                render: (value) => <div>{Number(value).toFixed(2) || 0} L</div>
            },
            {
                title: 'Fuel Issued During the Period',
                dataIndex: 'fuelIssued',
                key: 'fuelIssued',
                render: (value) => <div>{Number(value).toFixed(2) || 0} L</div>
            },
            {
                title: 'Balance at the end of the period',
                dataIndex: 'balance',
                key: 'balance',
                render: (value) => <div>{Number(value).toFixed(2) || 0} L</div>
            }
        ]
    }

    // A record adds fuel to the bowser (REFILL / manual increase) or issues it out
    // (DISPENSE / manual decrease). Used to pick the icon and row background.
    const isAddRecord = (type) => ['REFILL', 'REFILL_ADJ'].includes(type)
    const isAdjustment = (type) => ['REFILL_ADJ', 'DISPENSE_ADJ'].includes(type)

    const getChildTableColumns = () => {
        return [
            {
                title: 'Date',
                dataIndex: 'date',
                key: 'date',
                render: (date) => <div>{moment(date).format('DD-MM-YYYY HH:mm A')}</div>
            },
            {
                title: 'Particulars',
                dataIndex: 'particulars',
                key: 'particulars',
                render: (_, record) => {
                    const add = isAddRecord(record.bowserRefuelingType)
                    return (
                        <div>
                            <Icon
                                type={add ? 'plus-circle' : 'minus-circle'}
                                theme="filled"
                                style={{ color: add ? '#389e0d' : '#cf1322', marginRight: 6 }}
                            />
                            <span>{record.bowserRefuelingType || ''}</span>
                            {isAdjustment(record.bowserRefuelingType) &&
                                <Icon type="edit" style={{ color: '#d4380d', marginLeft: 6 }} />}
                        </div>
                    )
                }
            },
            {
                title: 'VCH',
                dataIndex: 'vch',
                key: 'vch',
                render: (_, record) => <div>{(record.refFuelRecord && record.refFuelRecord.voucherNo) || (record.refFuelAdjustment && record.refFuelAdjustment.voucherNo) || ''}</div>
            },
            {
                title: 'Asset Code',
                dataIndex: 'assetCode',
                key: 'assetCode',
                render: (_, record) => <div>{record.refAsset && (record.refAsset.assetCode || '-') + '/' + (record.refAsset.ownerCode || '-') + '/' + (record.refAsset.altName || '-')}</div>
            },
            {
                title: 'Qty',
                dataIndex: 'qty',
                key: 'qty',
                render: (_, record) => <div>
                    {['REFILL', 'REFILL_ADJ'].includes(record.bowserRefuelingType) ? record.quantityAdded : record.quantityRemoved} L
                </div>
            },
            {
                title: 'Balance',
                dataIndex: 'balance',
                key: 'balance',
                render: (_, record) => <div>
                    {Number(record.balance).toFixed(2) || 0} L
                </div>
            }
        ]
    }

    const expandedRowRender = (record) => {
        // console.log('expandedRowRender record:', record)

        let data = record.childrenData
        if (!data || data.length === 0) {
            return <div>No detailed data available for this bowser</div>
        }

        return (
            <Table
                columns={getChildTableColumns()}
                dataSource={data}
                pagination={false}
                size="small"
                bordered
                rowClassName={(record) => isAddRecord(record.bowserRefuelingType) ? 'bowser-row-add' : 'bowser-row-issue'}
            />
        )
    }

    const { getFieldDecorator } = form

    // console.log('assetData =>', assetData)

    return (
        <Spin spinning={loading}>
            <Card className="gx-card-metrics" title="Bowser Report">
                <Form
                    onSubmit={handleSubmit}
                    className="ant-advanced-search-form"
                    layout="inline"
                    labelAlign="left"
                >
                    <Row>
                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Form.Item key="location" label="Location" style={{ width: '100%' }}>
                                {getFieldDecorator('location', {
                                    rules: [{ required: false, message: 'Location is required!' }]
                                })(
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        showArrow
                                        allowClear
                                        onClear={() => {
                                            form.setFieldsValue({
                                                location: undefined,
                                                subLocation: undefined,
                                                assetCode: undefined
                                            })
                                        }}
                                        onChange={handleLocationChange}
                                        filterOption={(input, option) =>
                                            (option.props.children + '').toLowerCase().indexOf((input + '').toLowerCase()) >= 0
                                        }
                                        placeholder="Select Location"
                                    >
                                        <Select.Option key="all" value="all" disabled={form.getFieldValue('location') && form.getFieldValue('location').length > 0 && !form.getFieldValue('location').includes('all')}>
                                            All
                                        </Select.Option>
                                        {(authUser && authUser.locations || []).map(item => item && item.name !== 'SOLD' && (
                                            <Select.Option key={item._id} value={item._id} disabled={form.getFieldValue('location') && form.getFieldValue('location').includes('all')}>
                                                {item.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                )}
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Form.Item key="subLocation" label="Sub Location" style={{ width: '100%' }}>
                                {getFieldDecorator('subLocation')(
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        showArrow
                                        allowClear
                                        onClear={() => {
                                            form.setFieldsValue({ subLocation: undefined, assetCode: undefined })
                                        }}
                                        onChange={handleSubLocationChange}
                                        filterOption={(input, option) =>
                                            (option.props.children + '').toLowerCase().indexOf((input + '').toLowerCase()) >= 0
                                        }
                                        placeholder="Select Sub Location"
                                    >
                                        <Select.Option key="all" value="all" disabled={form.getFieldValue('subLocation') && form.getFieldValue('subLocation').length > 0 && !form.getFieldValue('subLocation').includes('all')}>
                                            All
                                        </Select.Option>
                                        {(authUser && authUser.clients || []).map(item => (
                                            <Select.Option key={item._id} value={item._id} disabled={form.getFieldValue('subLocation') && form.getFieldValue('subLocation').includes('all')}>
                                                {item.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                )}
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Form.Item key="assetType" label="Asset Type" style={{ width: '100%' }}>
                                {getFieldDecorator('assetType')(
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        showArrow
                                        allowClear
                                        onClear={() => {
                                            form.setFieldsValue({
                                                assetType: undefined,
                                                assetSubType: undefined,
                                                assetModel: undefined,
                                                assetCode: undefined
                                            })
                                        }}
                                        onChange={handleAssetTypeChange}
                                        filterOption={(input, option) =>
                                            (option.props.children + '').toLowerCase().indexOf((input + '').toLowerCase()) >= 0
                                        }
                                        placeholder="Select Asset Type"
                                    >
                                        <Select.Option key="all" value="all" disabled={form.getFieldValue('assetType') && form.getFieldValue('assetType').length > 0 && !form.getFieldValue('assetType').includes('all')}>
                                            All
                                        </Select.Option>
                                        {bowserAssetTypeOptions.map(item => (
                                            <Select.Option key={item._id} value={item._id} disabled={form.getFieldValue('assetType') && form.getFieldValue('assetType').includes('all')}>
                                                {item.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                )}
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Form.Item key="assetSubType" label="Asset Sub Type" style={{ width: '100%' }}>
                                {getFieldDecorator('assetSubType')(
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        showArrow
                                        allowClear
                                        onClear={() => {
                                            form.setFieldsValue({
                                                assetSubType: undefined,
                                                assetModel: undefined,
                                                assetCode: undefined
                                            })
                                        }}
                                        onChange={handleAssetSubTypeChange}
                                        filterOption={(input, option) =>
                                            (option.props.children + '').toLowerCase().indexOf((input + '').toLowerCase()) >= 0
                                        }
                                        placeholder="Select Asset Sub Type"
                                    >
                                        <Select.Option key="all" value="all" disabled={form.getFieldValue('assetSubType') && form.getFieldValue('assetSubType').length > 0 && !form.getFieldValue('assetSubType').includes('all')}>
                                            All
                                        </Select.Option>
                                        {bowserAssetSubTypeOptions
                                            .filter(item => {
                                                const selectedAssetType = form.getFieldValue('assetType')
                                                return !selectedAssetType || selectedAssetType.length === 0 || selectedAssetType.includes('all') || selectedAssetType.includes(item.assetType)
                                            })
                                            .map(item => (
                                                <Select.Option key={item._id} value={item._id} disabled={form.getFieldValue('assetSubType') && form.getFieldValue('assetSubType').includes('all')}>
                                                    {item.name}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                )}
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Form.Item key="assetModel" label="Asset Model" style={{ width: '100%' }}>
                                {getFieldDecorator('assetModel')(
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        showArrow
                                        allowClear
                                        onClear={() => {
                                            form.setFieldsValue({ assetModel: undefined })
                                            setSelectedAssets([])
                                        }}
                                        onChange={handleAssetModelChange}
                                        filterOption={(input, option) =>
                                            (option.props.children + '').toLowerCase().indexOf((input + '').toLowerCase()) >= 0
                                        }
                                        placeholder="Select Asset Model"
                                    >
                                        <Select.Option key="all" value="all" disabled={form.getFieldValue('assetModel') && form.getFieldValue('assetModel').length > 0 && !form.getFieldValue('assetModel').includes('all')}>
                                            All
                                        </Select.Option>
                                        {bowserAssetModelOptions
                                            .filter(item => {
                                                const selectedAssetType = form.getFieldValue('assetType')
                                                const selectedAssetSubType = form.getFieldValue('assetSubType')
                                                // If both type and subtype are empty or include 'all', show all models
                                                if ((!selectedAssetType || selectedAssetType.length === 0 || selectedAssetType.includes('all')) &&
                                                    (!selectedAssetSubType || selectedAssetSubType.length === 0 || selectedAssetSubType.includes('all'))) return true
                                                // If asset subtype is selected, show models for selected subtype
                                                if (selectedAssetSubType && selectedAssetSubType.length > 0 && !selectedAssetSubType.includes('all')) {
                                                    return selectedAssetSubType.includes(item.assetSubType)
                                                }
                                                // If only asset type is selected (subtype is empty or 'all'), show models whose
                                                // subtype belongs to the selected type
                                                if (selectedAssetType && selectedAssetType.length > 0 && !selectedAssetType.includes('all')) {
                                                    const modelAssetTypeId = subTypeIdToAssetTypeId[item.assetSubType && item.assetSubType.toString ? item.assetSubType.toString() : item.assetSubType]
                                                    return modelAssetTypeId && selectedAssetType.includes(modelAssetTypeId)
                                                }
                                                return true
                                            })
                                            .map(item => (
                                                <Select.Option key={item._id} value={item._id} disabled={form.getFieldValue('assetModel') && form.getFieldValue('assetModel').includes('all')}>
                                                    {item.name}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                )}
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Form.Item key="asset" label="Search Assets" style={{ width: '100%' }}>
                                {getFieldDecorator('asset', {
                                    rules: [{ required: false, message: 'Asset is required!' }],
                                })(
                                    <AssetSearch
                                        styleName="gx-lt-icon-search-bar-lg"
                                        placeholder="Search multiple Assets..."
                                        onChange={searchGlobalAssetData}
                                        assetData={
                                            assetData && assetData.filter((ast) => ast.assetModel && ast._id && bowserAssetIdSet.has(ast._id.toString()))
                                        } // only bowser assets (isBowser assetType) with an assetModel
                                        onSelect={handleAssetSelect}
                                        authUser={authUser}
                                        loading={assetSearchLoading}
                                        multiple={true}
                                    />
                                )}
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Form.Item
                                key="periodFromTo"
                                label="Select Period"
                                style={{ width: '100%' }}
                            >
                                {getFieldDecorator('periodFromTo', {
                                    rules: [{ required: true, message: 'Period From To is required!' }],
                                    initialValue: null
                                })(<RangePicker
                                    style={{ width: '100%' }}
                                    onChange={handlePeriodChange}
                                />)}
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                            <Form.Item style={{ marginTop: '25px' }}>
                                <span style={{ display: 'flex', flexDirection: 'row' }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading || downloadLoading}
                                        disabled={downloadLoading}
                                    >
                                        Generate Report
                                    </Button>
                                    <Button
                                        disabled={!bowserReportData || Object.keys(bowserReportData).length === 0 || downloadLoading}
                                        type="default"
                                        htmlType="button"
                                        loading={downloadLoading || loading}
                                        icon="download"
                                        onClick={downloadReport}
                                        style={{ marginLeft: '8px' }}
                                    >
                                        Download
                                    </Button>
                                </span>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 16px' }}>
                    <Switch checked={dayWiseSummary} onChange={setDayWiseSummary} />
                    <span>Enable day-wise summary</span>
                </div>

                {bowserReportData && Object.keys(bowserReportData).length > 0 ? (
                    dayWiseSummary ? (
                        dayWiseGroups.length > 0 ? dayWiseGroups.map(group => {
                            const dayRange = [moment(group.date).startOf('day').valueOf(), moment(group.date).endOf('day').valueOf()]
                            return (
                                <div key={group.date} style={{ marginTop: 24 }}>
                                    <h3>{moment(group.date).format('DD-MM-YYYY')}</h3>
                                    <Table
                                        dataSource={group.rows}
                                        columns={getTableColumns(dayRange)}
                                        expandedRowRender={(record) => expandedRowRender(record)}
                                        pagination={false}
                                        scroll={{ x: true }}
                                        bordered
                                    />
                                </div>
                            )
                        }) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )
                    ) : (
                        <Table
                            dataSource={Object.keys(bowserReportData).map(key => ({
                                key,
                                bowserAsset: bowserReportData[key].data.bowser,
                                bowserAssetCode: bowserReportData[key].data.bowser.assetCode,
                                openingBalance: (bowserReportData[key] && bowserReportData[key].data && bowserReportData[key].data.openingBalance) || 0,
                                fuelAdded: (bowserReportData[key] && bowserReportData[key].data && bowserReportData[key].data.totalAdded) || 0,
                                fuelIssued: (bowserReportData[key] && bowserReportData[key].data && bowserReportData[key].data.totalIssued) || 0,
                                balance: (bowserReportData[key] && bowserReportData[key].data && bowserReportData[key].data.balanceAtEnd) || 0,
                                childrenData: bowserReportData[key].records || [] // Add the records as childrenData for expandable rows
                            }))}
                            columns={getTableColumns()}
                            expandedRowRender={(record) => expandedRowRender(record)}
                            pagination={false}
                            scroll={{ x: true }}
                            bordered
                        />
                    )
                ) : loading ? (
                    <Skeleton active />
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </Card>
        </Spin>
    )
}

const mapStateToProps = ({ auth, fuel, asset, commonData }) => {
    return {
        authUser: auth.authUser,
        loading: commonData.loading,
        bowserAssets: asset.bowsers,
        bowserReportData: fuel.bowser_report_data,
        assetData: commonData.assetSearchData,
        assetSearchLoading: commonData.assetSearchLoading
    }
}

function mapDispatchToProps(dispatch) {
    return {
        fetchBowserAssets,
        getBowserReport,
        downloadBowserReport,
        searchGlobalAssetData
    }
}

const BowserReportForm = Form.create({ name: 'bowser-report' })(BowserReport)
export default connect(mapStateToProps, mapDispatchToProps())(BowserReportForm)
